import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import * as LanternApi from '../utilities/LanternApiClient';

const ParentClientCacheContext = createContext();

/**
 * Hook to access cached parent client data
 */
export const useParentClientCache = () => {
  const context = useContext(ParentClientCacheContext);
  if (!context) {
    throw new Error('useParentClientCache must be used within a ParentClientCacheProvider');
  }
  return context;
};

/**
 * Provider component to cache parent client data by hash reference
 */
export const ParentClientCacheProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const [loadingStates, setLoadingStates] = useState({});

  // Track ongoing requests to prevent duplicates
  const requestsRef = useRef({});

  /**
   * Fetch parent client by hash reference with caching
   */
  const getParentClientByHashReference = useCallback(
    async (hashReference, forceRefresh = false) => {
      if (!hashReference) {
        return null;
      }

      // Return cached data if available and not forcing refresh
      if (cache[hashReference] && !forceRefresh) {
        return cache[hashReference];
      }

      // Return existing request if already in progress
      if (requestsRef.current[hashReference]) {
        return requestsRef.current[hashReference];
      }

      setLoadingStates((prev) => ({ ...prev, [hashReference]: true }));

      try {
        const requestPromise = LanternApi.getParentClientByHashReference(hashReference);
        requestsRef.current[hashReference] = requestPromise;

        const response = await requestPromise;

        if (response.success) {
          const parentClient = response.body;
          setCache((prev) => ({ ...prev, [hashReference]: parentClient }));
          return parentClient;
        } else {
          console.warn('Failed to fetch parent client by hash reference:', hashReference);
          return null;
        }
      } catch (error) {
        console.error('Error fetching parent client:', error);
        return null;
      } finally {
        setLoadingStates((prev) => ({ ...prev, [hashReference]: false }));
        if (requestsRef.current[hashReference]) {
          delete requestsRef.current[hashReference];
        }
      }
    },
    [cache],
  );

  /**
   * Get loading state for a specific hash reference
   */
  const isLoading = useCallback(
    (hashReference) => {
      return loadingStates[hashReference] || false;
    },
    [loadingStates],
  );

  /**
   * Clear cache for a specific hash reference or all
   */
  const clearCache = useCallback((hashReference = null) => {
    if (hashReference) {
      setCache((prev) => {
        const newCache = { ...prev };
        delete newCache[hashReference];
        return newCache;
      });

      setLoadingStates((prev) => {
        const newStates = { ...prev };
        delete newStates[hashReference];
        return newStates;
      });

      if (requestsRef.current[hashReference]) {
        delete requestsRef.current[hashReference];
      }
    } else {
      setCache({});
      setLoadingStates({});
      requestsRef.current = {};
    }
  }, []);

  /**
   * Get cached parent client without making API call
   */
  const getCachedParentClient = useCallback(
    (hashReference) => {
      return cache[hashReference] || null;
    },
    [cache],
  );

  const contextValue = {
    // Methods
    getParentClientByHashReference,
    getCachedParentClient,
    clearCache,
    isLoading,

    // Data
    cache,
  };

  return <ParentClientCacheContext.Provider value={contextValue}>{children}</ParentClientCacheContext.Provider>;
};

export default ParentClientCacheContext;
